import { useState } from "react";
import { DataTable } from "../components/DataTable";
import type { DataTableColumn } from "../components/DataTable";

type Review = {
  id: string;
  customer: string;
  product: string;
  rating: number;
  comment: string;
  date: string;
  status: "published" | "pending" | "rejected";
};

const initialReviews: Review[] = [
  { id:"r1", customer:"Priya Sharma", product:"Polki Bridal Necklace", rating:5, comment:"Absolutely stunning! The craftsmanship is exquisite and the packaging was beautiful.", date:"2025-04-28", status:"published" },
  { id:"r2", customer:"Anjali Singh", product:"Kundan Choker Set", rating:4, comment:"Very beautiful set. Slightly heavy but perfect for weddings.", date:"2025-04-25", status:"published" },
  { id:"r3", customer:"Meena Patel", product:"Temple Gold Earrings", rating:3, comment:"Good quality but color was slightly different from photos.", date:"2025-04-22", status:"pending" },
  { id:"r4", customer:"Rekha Gupta", product:"Polki Bridal Necklace", rating:5, comment:"Best purchase ever! Highly recommend to all brides.", date:"2025-04-20", status:"published" },
  { id:"r5", customer:"Sunita Rao", product:"Kundan Choker Set", rating:2, comment:"Delivery was very late and box was damaged.", date:"2025-04-18", status:"rejected" },
];

const statusStyle: Record<Review["status"], { bg: string; color: string }> = {
  published: { bg:"#dcfce7", color:"#166534" },
  pending:   { bg:"#fef3c7", color:"#92400e" },
  rejected:  { bg:"#fee2e2", color:"#991b1b" },
};

const stars = (n: number) =>
  Array.from({ length: 5 }, (_, i) => (
    <span key={i} className={i < n ? "rvw-star-on" : "rvw-star-off"}>★</span>
  ));

export function ReviewsPage() {
  const [reviews, setReviews] = useState<Review[]>(initialReviews);
  const [filterStatus, setFilterStatus] = useState<"all" | Review["status"]>("all");

  function updateStatus(id: string, status: Review["status"]) {
    setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
  }
  function deleteReview(id: string) {
    setReviews((prev) => prev.filter((r) => r.id !== id));
  }

  const filteredRows =
    filterStatus === "all"
      ? (reviews as unknown as Record<string, unknown>[])
      : (reviews.filter((r) => r.status === filterStatus) as unknown as Record<string, unknown>[]);

  const columns: DataTableColumn<Record<string, unknown>>[] = [
    {
      key: "customer",
      label: "Customer",
      sortable: true,
      render: (row) => (
        <div>
          <div className="rvw-cust">{String(row.customer)}</div>
          <div className="rvw-date">{String(row.date)}</div>
        </div>
      ),
    },
    { key: "product", label: "Product", sortable: true },
    {
      key: "rating",
      label: "Rating",
      sortable: true,
      render: (row) => <div className="rvw-stars">{stars(Number(row.rating))}</div>,
    },
    {
      key: "comment",
      label: "Comment",
      render: (row) => (
        <span className="rvw-comment">"{String(row.comment)}"</span>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => {
        const s = row.status as Review["status"];
        return (
          <span className="pg-badge" style={{ background: statusStyle[s].bg, color: statusStyle[s].color }}>
            {s}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      className: "text-end",
      render: (row) => {
        const id = String(row.id);
        const s = row.status as Review["status"];
        return (
          <div className="pg-actions">
            {s !== "published" && (
              <button type="button" className="pg-btn-resolve" onClick={() => updateStatus(id, "published")}>Approve</button>
            )}
            {s !== "rejected" && (
              <button type="button" className="pg-btn-reject" onClick={() => updateStatus(id, "rejected")}>Reject</button>
            )}
            <button type="button" className="pg-btn-neutral" onClick={() => deleteReview(id)}>Delete</button>
          </div>
        );
      },
    },
  ];

  const filterBtns: { key: "all" | Review["status"]; label: string }[] = [
    { key: "all", label: "All" },
    { key: "published", label: "Published" },
    { key: "pending", label: "Pending" },
    { key: "rejected", label: "Rejected" },
  ];

  const avg = reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : "—";

  return (
    <div className="d-grid gap-4">
      <div className="pg-stats-grid">
        {[
          { label: "Total Reviews", val: reviews.length, bg: "#fef3c7", color: "#92400e" },
          { label: "Published", val: reviews.filter((r) => r.status === "published").length, bg: "#dcfce7", color: "#166534" },
          { label: "Pending", val: reviews.filter((r) => r.status === "pending").length, bg: "#dbeafe", color: "#1d4ed8" },
          { label: "Avg Rating", val: `${avg} ★`, bg: "#fce7f3", color: "#9d174d" },
        ].map((s) => (
          <div key={s.label} className="pg-stat-card" style={{ background: s.bg }}>
            <div className="pg-stat-label" style={{ color: s.color }}>{s.label}</div>
            <div className="pg-stat-value" style={{ color: s.color }}>{s.val}</div>
          </div>
        ))}
      </div>

      <div className="pg-filter-bar">
        {filterBtns.map((f) => (
          <button key={f.key} type="button" onClick={() => setFilterStatus(f.key)}
            className={`pg-filter-btn ${filterStatus === f.key ? "active" : ""}`}>
            {f.label}
          </button>
        ))}
      </div>

      <DataTable
        title="Customer Reviews"
        rows={filteredRows}
        columns={columns}
        rowKey={(row) => String(row.id)}
        searchPlaceholder="Search by customer or product..."
        exportFileName="reviews"
        onDeleteRow={(id) => deleteReview(id)}
        deleteItemLabel="Review"
        pageSize={6}
      />
    </div>
  );
}
